import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from 'react';
import { PageHeader } from '../components/shared/PageHeader';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { ErrorAlert } from '../components/shared/ErrorAlert';
import { ConfirmDialog } from '../components/shared/ConfirmDialog';
import { rebaselineApi, } from '../api/rebaselineApi';
export const RebaselineManagementPage = () => {
    const [traits, setTraits] = useState([]);
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    // Selected traits for targeted rebaseline
    const [selectedTraits, setSelectedTraits] = useState(new Set());
    // Interval override editing
    const [editingInterval, setEditingInterval] = useState(false);
    const [intervalInput, setIntervalInput] = useState('');
    // Confirm dialog state
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState(null);
    const loadData = async () => {
        try {
            setLoading(true);
            setError(null);
            const [traitsData, scheduleData] = await Promise.all([
                rebaselineApi.getRebaselineStatus(),
                rebaselineApi.getRebaselineSchedule(),
            ]);
            setTraits(traitsData);
            setSchedule(scheduleData);
        }
        catch (err) {
            setError(err.response?.data?.message || 'Failed to load rebaseline data');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        loadData();
    }, []);
    // Drift summary counts
    const driftCount = useMemo(() => traits.filter((t) => t.hasDrift).length, [traits]);
    const handleTriggerAll = () => {
        setConfirmConfig({
            title: 'Trigger Full Rebaseline',
            message: 'This will rebaseline all 31 axes and 15 shadow traits. This may take a few minutes. Continue?',
            variant: 'primary',
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    await rebaselineApi.triggerRebaseline();
                    await loadData();
                }
                catch (err) {
                    setError(err.response?.data?.message || 'Failed to trigger rebaseline');
                }
                finally {
                    setActionLoading(false);
                }
            },
        });
        setConfirmOpen(true);
    };
    const handleTriggerSelected = () => {
        if (selectedTraits.size === 0)
            return;
        const traitNames = traits
            .filter((t) => selectedTraits.has(t.traitId))
            .map((t) => t.displayName)
            .join(', ');
        setConfirmConfig({
            title: 'Trigger Selected Rebaseline',
            message: `Rebaseline ${selectedTraits.size} trait(s): ${traitNames}?`,
            variant: 'primary',
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    for (const traitId of selectedTraits) {
                        const trait = traits.find((t) => t.traitId === traitId);
                        if (trait) {
                            await rebaselineApi.triggerRebaseline(traitId, trait.traitType);
                        }
                    }
                    setSelectedTraits(new Set());
                    await loadData();
                }
                catch (err) {
                    setError(err.response?.data?.message || 'Failed to trigger rebaseline');
                }
                finally {
                    setActionLoading(false);
                }
            },
        });
        setConfirmOpen(true);
    };
    const handleOverrideInterval = () => {
        const days = parseInt(intervalInput, 10);
        if (isNaN(days) || days < 1) {
            setError('Interval must be a positive number');
            return;
        }
        setConfirmConfig({
            title: 'Override Rebaseline Interval',
            message: `Set rebaseline interval to ${days} days? This overrides the auto-calculated interval.`,
            variant: 'primary',
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    await rebaselineApi.updateRebaselineSchedule(days);
                    setEditingInterval(false);
                    setIntervalInput('');
                    await loadData();
                }
                catch (err) {
                    setError(err.response?.data?.message || 'Failed to update schedule');
                }
                finally {
                    setActionLoading(false);
                }
            },
        });
        setConfirmOpen(true);
    };
    const handleResetToAuto = () => {
        setConfirmConfig({
            title: 'Reset to Auto-Calculated Interval',
            message: 'Remove the manual override and return to the auto-calculated rebaseline interval?',
            variant: 'primary',
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    await rebaselineApi.updateRebaselineSchedule(null);
                    setEditingInterval(false);
                    setIntervalInput('');
                    await loadData();
                }
                catch (err) {
                    setError(err.response?.data?.message || 'Failed to reset schedule');
                }
                finally {
                    setActionLoading(false);
                }
            },
        });
        setConfirmOpen(true);
    };
    const toggleTraitSelection = (traitId) => {
        setSelectedTraits((prev) => {
            const next = new Set(prev);
            if (next.has(traitId)) {
                next.delete(traitId);
            }
            else {
                next.add(traitId);
            }
            return next;
        });
    };
    const toggleSelectAll = () => {
        if (selectedTraits.size === traits.length) {
            setSelectedTraits(new Set());
        }
        else {
            setSelectedTraits(new Set(traits.map((t) => t.traitId)));
        }
    };
    const handleConfirm = async () => {
        setConfirmOpen(false);
        if (confirmConfig?.onConfirm) {
            await confirmConfig.onConfirm();
        }
        setConfirmConfig(null);
    };
    const handleCancelConfirm = () => {
        setConfirmOpen(false);
        setConfirmConfig(null);
    };
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center h-64", children: _jsx(LoadingSpinner, { size: "lg" }) }));
    }
    if (error && traits.length === 0) {
        return _jsx(ErrorAlert, { message: error, onRetry: loadData });
    }
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Rebaseline Management", subtitle: "Monitor population statistics drift and manage rebaseline scheduling", action: _jsxs("div", { className: "flex gap-2", children: [_jsxs("button", { onClick: handleTriggerSelected, disabled: actionLoading || selectedTraits.size === 0, className: "px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed", children: ["Trigger Selected (", selectedTraits.size, ")"] }), _jsx("button", { onClick: handleTriggerAll, disabled: actionLoading, className: "px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed", children: actionLoading ? 'Running…' : 'Trigger All' })] }) }), error && _jsx(ErrorAlert, { message: error, onDismiss: () => setError(null) }), schedule && (_jsxs("div", { className: "card mb-6", children: [_jsx("h2", { className: "text-xl font-semibold mb-4", children: "Schedule" }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4", children: [_jsxs("div", { className: "bg-gray-50 rounded-lg p-4", children: [_jsx("p", { className: "text-xs font-medium text-gray-500 uppercase", children: "Active Profiled Users" }), _jsx("p", { className: "mt-1 text-2xl font-semibold text-gray-900", children: schedule.activeUserCount.toLocaleString() })] }), _jsxs("div", { className: "bg-gray-50 rounded-lg p-4", children: [_jsx("p", { className: "text-xs font-medium text-gray-500 uppercase", children: "Effective Interval" }), _jsxs("p", { className: "mt-1 text-2xl font-semibold text-gray-900", children: [schedule.effectiveIntervalDays, " days"] }), schedule.adminOverrideDays && (_jsxs("p", { className: "text-xs text-yellow-600 mt-1", children: ["Override active (auto: ", schedule.autoIntervalDays, "d)"] }))] }), _jsxs("div", { className: "bg-gray-50 rounded-lg p-4", children: [_jsx("p", { className: "text-xs font-medium text-gray-500 uppercase", children: "Next Scheduled Run" }), _jsx("p", { className: "mt-1 text-lg font-semibold text-gray-900", children: schedule.nextScheduledRun
                                            ? new Date(schedule.nextScheduledRun).toLocaleDateString()
                                            : '—' })] }), _jsxs("div", { className: "bg-gray-50 rounded-lg p-4", children: [_jsx("p", { className: "text-xs font-medium text-gray-500 uppercase", children: "Drift Detected" }), _jsxs("p", { className: `mt-1 text-2xl font-semibold ${driftCount > 0 ? 'text-red-600' : 'text-green-600'}`, children: [driftCount, " trait", driftCount !== 1 ? 's' : ''] })] })] }), _jsxs("div", { className: "flex items-center gap-3 border-t border-gray-200 pt-4", children: [editingInterval ? (_jsxs(_Fragment, { children: [_jsx("input", { type: "number", min: "1", value: intervalInput, onChange: (e) => setIntervalInput(e.target.value), placeholder: "Days", className: "w-24 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm" }), _jsx("button", { onClick: handleOverrideInterval, disabled: actionLoading, className: "px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50", children: "Set Override" }), _jsx("button", { onClick: () => {
                                            setEditingInterval(false);
                                            setIntervalInput('');
                                        }, className: "px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900", children: "Cancel" })] })) : (_jsxs(_Fragment, { children: [_jsx("button", { onClick: () => setEditingInterval(true), disabled: actionLoading, className: "px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-900", children: "Override Interval" }), schedule.adminOverrideDays && (_jsx("button", { onClick: handleResetToAuto, disabled: actionLoading, className: "px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900", children: "Reset to Auto" }))] })), schedule.lastRunTimestamp && (_jsxs("span", { className: "ml-auto text-xs text-gray-500", children: ["Last run:", ' ', new Date(schedule.lastRunTimestamp).toLocaleString()] }))] })] })), _jsxs("div", { className: "card", children: [_jsx("h2", { className: "text-xl font-semibold mb-4", children: "Per-Trait Status" }), traits.length === 0 ? (_jsx("p", { className: "text-gray-500 text-center py-8", children: "No trait data available." })) : (_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "min-w-full divide-y divide-gray-200", children: [_jsx("thead", { className: "bg-gray-50", children: _jsxs("tr", { children: [_jsx("th", { className: "px-4 py-3 text-left", children: _jsx("input", { type: "checkbox", checked: selectedTraits.size === traits.length, onChange: toggleSelectAll, className: "rounded border-gray-300 text-primary-600 focus:ring-primary-500" }) }), _jsx("th", { className: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "Trait Name" }), _jsx("th", { className: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "Type" }), _jsx("th", { className: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider", children: "Last Rebaseline" }), _jsx("th", { className: "px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider", children: "Mean" }), _jsx("th", { className: "px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider", children: "Stddev" }), _jsx("th", { className: "px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider", children: "Users" }), _jsx("th", { className: "px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider", children: "Drift" })] }) }), _jsx("tbody", { className: "bg-white divide-y divide-gray-200", children: traits.map((trait) => (_jsxs("tr", { className: selectedTraits.has(trait.traitId)
                                            ? 'bg-primary-50'
                                            : '', children: [_jsx("td", { className: "px-4 py-3", children: _jsx("input", { type: "checkbox", checked: selectedTraits.has(trait.traitId), onChange: () => toggleTraitSelection(trait.traitId), className: "rounded border-gray-300 text-primary-600 focus:ring-primary-500" }) }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap", children: _jsx("span", { className: "text-sm font-medium text-gray-900", children: trait.displayName }) }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap", children: _jsx("span", { className: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${trait.traitType === 'axis'
                                                        ? 'bg-blue-100 text-blue-800'
                                                        : 'bg-purple-100 text-purple-800'}`, children: trait.traitType }) }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap text-sm text-gray-500", children: trait.lastRebaselineTimestamp
                                                    ? new Date(trait.lastRebaselineTimestamp).toLocaleDateString()
                                                    : '—' }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono", children: trait.currentMean !== null
                                                    ? trait.currentMean.toFixed(2)
                                                    : '—' }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono", children: trait.currentStddev !== null
                                                    ? trait.currentStddev.toFixed(2)
                                                    : '—' }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right", children: trait.userCount.toLocaleString() }), _jsx("td", { className: "px-4 py-3 whitespace-nowrap text-center", children: trait.hasDrift ? (_jsx("span", { className: "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200", title: `Mean shift: ${trait.meanShift.toFixed(1)}, Stddev shift: ${trait.stddevShiftPercent.toFixed(1)}%`, children: "Drift" })) : (_jsx("span", { className: "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200", children: "Stable" })) })] }, trait.traitId))) })] }) }))] }), _jsx(ConfirmDialog, { isOpen: confirmOpen, title: confirmConfig?.title || '', message: confirmConfig?.message || '', confirmLabel: "Confirm", cancelLabel: "Cancel", variant: confirmConfig?.variant || 'primary', onConfirm: handleConfirm, onCancel: handleCancelConfirm })] }));
};
